/**
 * Group Filtering Service
 * Handles complex filtering business logic for group creation
 * Uses existing API services following clean architecture
 */

import { AbstractService } from '@/services/core/serviceFactory';
import { clinicsGraphqlService, patientsGraphqlService } from '@/services/api';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterParams {
  clinicId: number;
  facilityIds?: number[];
  providerIds?: number[];
  primaryDx?: string[];
  secondaryDx?: string[];
  primaryInsurance?: string[];
  secondaryInsurance?: string[];
  insuranceType?: string[];
  programEligibility?: string[];
  lastVisitDate?: string;
  excludeAssignedGroups?: boolean;
}

export class GroupFilteringService extends AbstractService {

  /**
   * Load available clinics - delegates to API service
   */
  async loadClinics(): Promise<Array<{
    clinic_id: number;
    clinic_name: string;
    patient_clinic_timezone: string;
  }>> {
    this.requireAuth();
    try {
      return await clinicsGraphqlService.getAvailableClinics(this.context.hasuraToken!);
    } catch (error) {
      throw new Error('Failed to load clinics');
    }
  }

  /**
   * Load facilities for a clinic - delegates to API service
   */
  async loadFacilities(clinicId: number) {
    this.requireAuth();
    try {
      return await clinicsGraphqlService.getFacilitiesByClinic(clinicId, this.context.hasuraToken!);
    } catch (error) {
      throw new Error('Failed to load facilities');
    }
  }

  /**
   * Load providers for a clinic - delegates to API service
   */
  async loadProviders(clinicId: number) {
    this.requireAuth();
    try {
      return await clinicsGraphqlService.getProvidersByClinic(clinicId, this.context.hasuraToken!);
    } catch (error) {
      throw new Error('Failed to load providers');
    }
  }

  /**
   * Load providers for specific facilities - delegates to API service
   */
  async loadProvidersForFacilities(clinicId: number, facilityIds: number[]) {
    this.requireAuth();
    try {
      return await clinicsGraphqlService.getProvidersByClinicAndFacilities(clinicId, facilityIds, this.context.hasuraToken!);
    } catch (error) {
      throw new Error('Failed to load providers for facilities');
    }
  }

  /**
   * Apply filters to get patient count and IDs - uses API service with business logic
   */
  async applyFilters(filterParams: FilterParams): Promise<{ patientIds: number[]; count: number }> {
    this.requireAuth();

    try {
      // Build where clause with business logic
      const whereConditions: any = {
        ccm_status: { _nin: [1, 2, 3, 7, 8, 9, 10] },
        clinic_id: { _eq: filterParams.clinicId }
      };

      // Apply filter conditions
      if (filterParams.facilityIds?.length) {
        whereConditions.facility_id = { _in: filterParams.facilityIds };
      }
      if (filterParams.providerIds?.length) {
        whereConditions.provider_id = { _in: filterParams.providerIds };
      }
      if (filterParams.primaryDx?.length) {
        whereConditions.primary_dx = { _in: filterParams.primaryDx };
      }
      if (filterParams.secondaryDx?.length) {
        whereConditions.secondary_dx = { _in: filterParams.secondaryDx };
      }
      if (filterParams.primaryInsurance?.length) {
        whereConditions.primary_insurance_provider = { _in: filterParams.primaryInsurance };
      }
      if (filterParams.secondaryInsurance?.length) {
        whereConditions.secondary_insurance_provider = { _in: filterParams.secondaryInsurance };
      }
      if (filterParams.insuranceType?.length) {
        whereConditions.insurance_type = { _in: filterParams.insuranceType };
      }

      // Program eligibility flags
      if (filterParams.programEligibility?.includes('PCM')) {
        whereConditions.pcm_flag = { _eq: true };
      }
      if (filterParams.programEligibility?.includes('CCM')) {
        whereConditions.ccm_flag = { _eq: true };
      }
      if (filterParams.programEligibility?.includes('RPM')) {
        whereConditions.rpm_flag = { _eq: true };
      }

      if (filterParams.lastVisitDate) {
        whereConditions.last_seen_date = { _gt: filterParams.lastVisitDate };
      }

      // Use API service for data fetching
      const result = await patientsGraphqlService.getConsolidatedData(
        this.context.hasuraToken!,
        whereConditions
      );

      if (!result.data?.pep_vw_consolidated_data) {
        return { patientIds: [], count: 0 };
      }

      let patientData = result.data.pep_vw_consolidated_data;

      // Business logic: Apply exclude assigned groups filter if enabled
      if (filterParams.excludeAssignedGroups) {
        patientData = patientData.filter(item => !item.has_active_group);
      }

      const patientIds = patientData.map(item => item.patient_clinic_data_id);

      return { patientIds, count: patientIds.length };
    } catch (error) {
      throw new Error('Failed to apply filters');
    }
  }

  /**
   * Get filter options from patient data - business logic for filtering
   */
  async getFilterOptions(clinicId: number, facilityIds?: number[], providerIds?: number[]): Promise<{
    primaryDx: FilterOption[];
    secondaryDx: FilterOption[];
    primaryInsurance: FilterOption[];
    secondaryInsurance: FilterOption[];
    insuranceType: FilterOption[];
  }> {
    this.requireAuth();

    try {
      const whereConditions: any = {
        ccm_status: { _nin: [1, 2, 3, 7, 8, 9, 10] },
        clinic_id: { _eq: clinicId }
      };

      if (facilityIds?.length) {
        whereConditions.facility_id = { _in: facilityIds };
      }
      if (providerIds?.length) {
        whereConditions.provider_id = { _in: providerIds };
      }

      const result = await patientsGraphqlService.getConsolidatedData(
        this.context.hasuraToken!,
        whereConditions
      );

      if (!result.data?.pep_vw_consolidated_data) {
        return { primaryDx: [], secondaryDx: [], primaryInsurance: [], secondaryInsurance: [], insuranceType: [] };
      }

      const data = result.data.pep_vw_consolidated_data;

      // Business logic: Extract unique values and format as options
      const primaryDx = [...new Set(data.map(item => item.primary_dx).filter(Boolean))]
        .map(dx => ({ value: dx as string, label: dx as string }));

      const secondaryDx = [...new Set(data.map(item => item.secondary_dx).filter(Boolean))]
        .map(dx => ({ value: dx as string, label: dx as string }));

      const primaryInsurance = [...new Set(data.map(item => item.primary_insurance_provider).filter(Boolean))]
        .map(insurance => ({ value: insurance as string, label: insurance as string }));

      const secondaryInsurance = [...new Set(data.map(item => item.secondary_insurance_provider).filter(Boolean))]
        .map(insurance => ({ value: insurance as string, label: insurance as string }));

      const insuranceType = [...new Set(data.map(item => item.insurance_type).filter(Boolean))]
        .map(type => ({ value: type as string, label: type as string }));

      return { primaryDx, secondaryDx, primaryInsurance, secondaryInsurance, insuranceType };
    } catch (error) {
      throw new Error('Failed to get filter options');
    }
  }

  /**
   * Search patients - delegates to API service with business logic
   */
  async searchPatients(clinicId: number, searchTerm: string): Promise<string[]> {
    this.requireAuth();

    if (searchTerm.length < 2) {
      return [];
    }

    try {
      const result = await patientsGraphqlService.getConsolidatedData(
        this.context.hasuraToken!,
        {
          ccm_status: { _nin: [1, 2, 3, 7, 8, 9, 10] },
          search_string: { _iregex: `.*${searchTerm}.*` },
          clinic_id: { _eq: clinicId }
        },
        undefined,
        10
      );

      if (!result.data?.pep_vw_consolidated_data) {
        return [];
      }

      // Business logic: Format search results for display
      return result.data.pep_vw_consolidated_data.map(item =>
        `${item.full_name} (EMR: ${item.emr_id}) (ID: ${item.patient_clinic_data_id})`
      );
    } catch (error) {
      return [];
    }
  }

  /**
   * Get patients by IDs - delegates to API service
   */
  async getPatientsByIds(patientIds: number[]) {
    this.requireAuth();
    try {
      const result = await patientsGraphqlService.getConsolidatedData(
        this.context.hasuraToken!,
        { patient_clinic_data_id: { _in: patientIds } },
        [{ full_name: 'asc' }]
      );
      return result.data?.pep_vw_consolidated_data || [];
    } catch (error) {
      throw new Error('Failed to get patients by IDs');
    }
  }

  /**
   * Get patients by EMR IDs - delegates to API service
   */
  async getPatientsByEmrIds(emrIds: string[], clinicId: number) {
    this.requireAuth();
    try {
      const result = await patientsGraphqlService.getPatientsByEmrIds(this.context.hasuraToken!, emrIds, clinicId);
      return result.data?.pep_vw_consolidated_data || [];
    } catch (error) {
      throw new Error('Failed to get patients by EMR IDs');
    }
  }
}